import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, X, Save, Loader2, Users2,
  Eye, ExternalLink, Sparkles, AlertTriangle, MoreVertical,
  UserCheck, UserX, Ban, RotateCcw, ArrowRightCircle, MailCheck, MailWarning, MousePointerClick,
  BarChart3, Send,
} from "lucide-react";
import { WebinarAnalyticsOverview, WebinarAnalyticsDrilldown } from "./WebinarAnalytics";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { api, API, apiErrorMessage } from "../../lib/api";
import ExportButton from "./ExportButton";
import { exportPath } from "../../lib/exportFile";
import { leadHref } from "../../lib/leadLink";

const STATUS_LABELS = {
  draft: "Ciornă",
  published: "Publicat",
  full: "Complet",
  cancelled: "Anulat",
  completed: "Finalizat",
};
const STATUS_COLORS = {
  draft: "bg-[#6B7280]/15 text-[#373A45]",
  published: "bg-[#519872]/15 text-[#2F6B4E]",
  full: "bg-[#F5C76E]/30 text-[#7A5A1B]",
  cancelled: "bg-[#E63946]/15 text-[#7A1F26]",
  completed: "bg-[#3F8EBA]/15 text-[#1f5d80]",
};

// M905 — per-participant attendance status (separate from webinar status above).
const REG_STATUS_LABELS = {
  registered: "Înscris",
  attended: "Prezent",
  no_show: "Absent",
  cancelled: "Nu mai vine",
};
const REG_STATUS_COLORS = {
  registered: "bg-[#3F8EBA]/15 text-[#1f5d80]",
  attended: "bg-[#519872]/15 text-[#2F6B4E]",
  no_show: "bg-[#F5C76E]/30 text-[#7A5A1B]",
  cancelled: "bg-[#E63946]/15 text-[#7A1F26]",
};

/**
 * M890 — Admin tab for managing free webinarii (Phase 1).
 *
 * UI surface mirrors the GroupsManager layout for consistency: list on top,
 * modal for create/edit, drawer with registrations + CSV export. Partner
 * operators don't reach this component (gated in the sidebar).
 */
export default function WebinarsManager({ canMutate, currentRole }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [includePast, setIncludePast] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [drawerWebinar, setDrawerWebinar] = useState(null);
  const [analyticsOverview, setAnalyticsOverview] = useState(false);
  const [analyticsWebinar, setAnalyticsWebinar] = useState(null);

  const isOwner = currentRole === "owner";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/webinars", { params: { include_past: includePast } });
      setItems(r.data?.webinars || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nu am putut încărca webinariile.");
    } finally {
      setLoading(false);
    }
  }, [includePast]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setShowForm(true); };
  const openEdit = (w) => { setEditing(w); setShowForm(true); };

  const handleDelete = async (w) => {
    if (!window.confirm(`Ștergi webinarul „${w.title}”? Va dispărea de pe site, dar înscrierile rămân în baza de date.`)) return;
    try {
      await api.delete(`/admin/webinars/${w.id}`);
      toast.success("Webinar șters.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la ștergere.");
    }
  };

  if (analyticsWebinar) {
    return (
      <div data-testid="webinars-manager">
        <WebinarAnalyticsDrilldown
          webinar={analyticsWebinar}
          onBack={() => setAnalyticsWebinar(null)}
        />
      </div>
    );
  }
  if (analyticsOverview) {
    return (
      <div data-testid="webinars-manager">
        <WebinarAnalyticsOverview
          onBack={() => setAnalyticsOverview(false)}
          onSelectWebinar={(w) => { setAnalyticsOverview(false); setAnalyticsWebinar(w); }}
        />
      </div>
    );
  }

  return (
    <div data-testid="webinars-manager">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#373A45] inline-flex items-center gap-2">
            <Sparkles size={20} className="text-[#965EA7]" /> Webinarii
          </h2>
          <p className="text-sm text-[#6B7280] mt-0.5">
            Sesiuni gratuite de prezentare. Publicat = vizibil pe `/webinarii`.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-2 text-sm text-[#373A45]" data-testid="webinars-include-past">
            <input type="checkbox" checked={includePast} onChange={(e) => setIncludePast(e.target.checked)} />
            Include finalizate
          </label>
          <button
            onClick={() => setAnalyticsOverview(true)}
            data-testid="webinars-analytics-button"
            className="inline-flex items-center gap-2 rounded-full border border-[#965EA7]/40 text-[#7A4986] hover:bg-[#F0E7F4] font-semibold px-4 py-2 text-sm"
          >
            <BarChart3 size={16} /> Analytics
          </button>
          {canMutate && (
            <button
              onClick={openCreate}
              data-testid="webinars-create-button"
              className="inline-flex items-center gap-2 rounded-full bg-[#965EA7] hover:bg-[#7A4986] text-white font-semibold px-4 py-2 text-sm"
            >
              <Plus size={16} /> Webinar nou
            </button>
          )}
        </div>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB] text-[#6B7280] text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Titlu</th>
                <th className="text-left px-4 py-3">Data</th>
                <th className="text-left px-4 py-3">Înscriși / Capacitate</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Acțiuni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F1F1F1]">
              {loading ? (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-[#6B7280]">
                  <Loader2 className="inline animate-spin" size={16} /> Se încarcă...
                </td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-[#6B7280]">
                  Niciun webinar. {canMutate && "Adaugă primul."}
                </td></tr>
              ) : items.map((w) => (
                <tr key={w.id} className="hover:bg-[#FAFAFB]" data-testid={`webinar-row-${w.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-[#373A45]">{w.title}</div>
                    <div className="text-xs text-[#9CA3AF]">{w.start_time} · {w.duration_minutes} min · {w.location_type === "online" ? "Online" : "Fizic"}</div>
                  </td>
                  <td className="px-4 py-3 text-[#373A45]">{w.date}</td>
                  <td className="px-4 py-3">
                    <span className="font-semibold text-[#373A45]">{w.registrations_count || 0}</span>
                    <span className="text-[#9CA3AF]">{w.capacity > 0 ? ` / ${w.capacity}` : " / ∞"}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_COLORS[w.status] || ""}`}>
                      {STATUS_LABELS[w.status] || w.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => setDrawerWebinar(w)}
                      data-testid={`webinar-row-view-${w.id}`}
                      className="inline-flex items-center gap-1 text-[#3F8EBA] hover:text-[#1f5d80] text-xs font-semibold mr-3"
                    >
                      <Users2 size={14} /> Înscriși
                    </button>
                    <button
                      onClick={() => setAnalyticsWebinar(w)}
                      data-testid={`webinar-row-analytics-${w.id}`}
                      className="inline-flex items-center gap-1 text-[#965EA7] hover:text-[#7A4986] text-xs font-semibold mr-3"
                    >
                      <BarChart3 size={14} /> Analytics
                    </button>
                    {canMutate && (
                      <>
                        <button
                          onClick={async () => {
                            if (!window.confirm(`Trimiți emailul de follow-up (cu link de adeverință) către participanții la „${w.title}"?`)) return;
                            try {
                              const r = await api.post(`/admin/webinars/${w.id}/send-followup`);
                              // The server sends in the background and answers how many it will reach (19.10.2026).
                              const n = r.data?.candidates || 0;
                              toast.success(n === 0 ? "Toți participanții au primit deja follow-up-ul."
                                : `Follow-up-ul pleacă acum către ${n === 1 ? "un participant" : `${n} participanți`}.`);
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || "Eroare la trimiterea follow-up.");
                            }
                          }}
                          data-testid={`webinar-row-followup-${w.id}`}
                          className="inline-flex items-center gap-1 text-[#519872] hover:text-[#3f7a5b] text-xs font-semibold mr-3"
                        >
                          <MailCheck size={14} /> Follow-up
                        </button>
                        <button
                          onClick={async () => {
                            const email = window.prompt(`Trimite un follow-up de TEST pentru „${w.title}".\nEmailul folosește linkurile reale ale webinarului (înregistrare, feedback, înscriere la curs), dar NU marchează nimic și NU afectează participanții.\n\nAdresa de email:`);
                            if (!email || !email.trim()) return;
                            try {
                              const r = await api.post(`/admin/webinars/${w.id}/send-followup-test`, { email: email.trim() });
                              toast.success(`Follow-up de test trimis către ${r.data?.email}.`);
                            } catch (e) {
                              toast.error(apiErrorMessage(e, "Eroare la trimiterea follow-up-ului de test."));
                            }
                          }}
                          data-testid={`webinar-row-followup-test-${w.id}`}
                          className="inline-flex items-center gap-1 text-[#6B7280] hover:text-[#373A45] text-xs font-semibold mr-3"
                        >
                          <Send size={14} /> Test
                        </button>
                        <button
                          onClick={() => openEdit(w)}
                          data-testid={`webinar-row-edit-${w.id}`}
                          className="inline-flex items-center gap-1 text-[#965EA7] hover:text-[#7A4986] text-xs font-semibold mr-3"
                        >
                          <Pencil size={14} /> Editează
                        </button>
                        {isOwner && (
                          <button
                            onClick={() => handleDelete(w)}
                            data-testid={`webinar-row-delete-${w.id}`}
                            className="inline-flex items-center gap-1 text-[#E63946] hover:text-[#a52833] text-xs font-semibold"
                          >
                            <Trash2 size={14} /> Șterge
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <WebinarFormModal
          initial={editing}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}
      {drawerWebinar && (
        <RegistrationsDrawer
          webinar={drawerWebinar}
          onClose={() => setDrawerWebinar(null)}
          canMutate={canMutate}
        />
      )}
    </div>
  );
}

// ===========================================================================
// Form modal (Create / Edit)
// ===========================================================================

const emptyForm = {
  title: "", slug: "", short_description: "", full_description: "",
  date: "", start_time: "18:00", duration_minutes: 60,
  capacity: 100,
  location_type: "online",
  meet_link: "", physical_address: "",
  speaker_name: "", speaker_title: "", speaker_photo_url: "", speaker_bio: "",
  organizer_name: "Fundația pentru Educație Digitală",
  recording_url: "", preenrollment_url: "", feedback_url: "",
  status: "draft",
  tags: [],
};

function WebinarFormModal({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({ ...emptyForm, ...(initial || {}) }));
  const [loading, setLoading] = useState(false);
  const isEdit = !!initial;

  const update = (k) => (e) => {
    const v = e.target.type === "number" ? parseInt(e.target.value, 10) || 0 : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.date || !form.start_time) {
      toast.error("Completează titlul, data și ora de start.");
      return;
    }
    if (form.location_type === "online" && form.status === "published" && !form.meet_link.trim()) {
      toast.error("Pentru webinarii online publicate, linkul de acces este obligatoriu (va fi trimis participanților înainte de eveniment, nu se publică pe site).");
      return;
    }
    setLoading(true);
    try {
      if (isEdit) {
        await api.put(`/admin/webinars/${initial.id}`, form);
        toast.success("Webinar actualizat.");
      } else {
        await api.post("/admin/webinars", form);
        toast.success("Webinar creat.");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la salvare.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="bg-white rounded-3xl border border-[#E5E7EB] shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="webinar-form-modal"
      >
        <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB] sticky top-0 bg-white">
          <h3 className="font-display text-xl font-bold text-[#373A45]">
            {isEdit ? "Editează webinarul" : "Webinar nou"}
          </h3>
          <button onClick={onClose} className="text-[#6B7280] hover:text-[#373A45]"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="p-5 md:p-6 space-y-5">
          <Section title="General">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Titlu *">
                <input required value={form.title} onChange={update("title")} className={inp} data-testid="webinar-form-title" />
              </Field>
              <Field label="Slug (opțional)">
                <input value={form.slug || ""} onChange={update("slug")} placeholder="auto-generat" className={inp} data-testid="webinar-form-slug" />
              </Field>
              <Field label="Descriere scurtă" wide>
                <textarea rows={2} value={form.short_description} onChange={update("short_description")} className={inp} />
              </Field>
              <Field label="Descriere completă" wide>
                <textarea rows={4} value={form.full_description} onChange={update("full_description")} className={inp} />
              </Field>
            </div>
          </Section>

          <Section title="Programare">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Data *">
                <input required type="date" value={form.date} onChange={update("date")} className={inp} data-testid="webinar-form-date" />
              </Field>
              <Field label="Ora de start *">
                <input required type="time" value={form.start_time} onChange={update("start_time")} className={inp} />
              </Field>
              <Field label="Durată (min)">
                <input type="number" min="15" max="480" value={form.duration_minutes} onChange={update("duration_minutes")} className={inp} />
              </Field>
            </div>
          </Section>

          <Section title="Participare">
            <Field label="Capacitate (0 = nelimitat)">
              <input type="number" min="0" value={form.capacity} onChange={update("capacity")} className={inp} />
            </Field>
          </Section>

          <Section title="Locație">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Format">
                <select value={form.location_type} onChange={update("location_type")} className={inp}>
                  <option value="online">Online</option>
                  <option value="physical">Fizic</option>
                </select>
              </Field>
              {form.location_type === "online" ? (
                <Field label="Link Meet / Zoom (NU se publică pe site)">
                  <input value={form.meet_link} onChange={update("meet_link")} placeholder="https://meet.google.com/..." className={inp} />
                </Field>
              ) : (
                <Field label="Adresă fizică">
                  <input value={form.physical_address} onChange={update("physical_address")} className={inp} />
                </Field>
              )}
            </div>
            <p className="text-xs text-[#6B7280] mt-2 inline-flex items-center gap-1">
              <AlertTriangle size={12} /> Linkul de acces se trimite participanților într-un email separat, înainte de eveniment — niciodată pe site.
            </p>
          </Section>

          <Section title="Speaker">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Nume">
                <input value={form.speaker_name} onChange={update("speaker_name")} className={inp} />
              </Field>
              <Field label="Funcție">
                <input value={form.speaker_title} onChange={update("speaker_title")} className={inp} />
              </Field>
              <Field label="URL fotografie">
                <input value={form.speaker_photo_url} onChange={update("speaker_photo_url")} placeholder="https://..." className={inp} />
              </Field>
            </div>
            <p className="text-xs text-[#6B7280] mt-2">
              Link direct către fișierul imagine (https://..., .jpg/.png/.webp). Pătrată, min. 200×200px. Dacă lipsește sau e greșit, se afișează inițiala numelui.
            </p>
            <div className="mt-4">
              <Field label="Descriere instructor (bio) — apare pe pagina dedicată">
                <textarea rows={4} value={form.speaker_bio} onChange={update("speaker_bio")} placeholder="Experiență, realizări, de ce e relevant pentru acest webinar..." className={inp} data-testid="webinar-form-speaker-bio" />
              </Field>
            </div>
          </Section>

          <Section title="Follow-up (după webinar)">
            <Field label="Link înregistrare — apare în emailul de follow-up (gol → ai.fped.org)">
              <input value={form.recording_url} onChange={update("recording_url")} placeholder="https://youtube.com/... sau Drive" className={inp} data-testid="webinar-form-recording-url" />
            </Field>
            <div className="mt-4">
              <Field label="Link înscriere curs — butonul „Înscrie-te la curs” din emailul de follow-up (gol → ai.fped.org)">
                <input value={form.preenrollment_url} onChange={update("preenrollment_url")} placeholder="https://ai.fped.org" className={inp} data-testid="webinar-form-preenrollment-url" />
              </Field>
            </div>
            <div className="mt-4">
              <Field label="Link formular feedback — apare în emailul de follow-up (gol → secțiunea nu apare)">
                <input value={form.feedback_url} onChange={update("feedback_url")} placeholder="https://forms.gle/..." className={inp} data-testid="webinar-form-feedback-url" />
              </Field>
            </div>
          </Section>

          <Section title="Publicare">
            <Field label="Status">
              <select value={form.status} onChange={update("status")} className={inp} data-testid="webinar-form-status">
                <option value="draft">Ciornă</option>
                <option value="published">Publicat (vizibil pe /webinarii)</option>
                <option value="full">Complet</option>
                <option value="cancelled">Anulat</option>
                <option value="completed">Finalizat</option>
              </select>
            </Field>
          </Section>

          <div className="flex justify-end gap-2 pt-3 border-t border-[#E5E7EB]">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-full text-[#6B7280] hover:text-[#373A45] text-sm">Anulează</button>
            <button
              type="submit"
              disabled={loading}
              data-testid="webinar-form-save"
              className="inline-flex items-center gap-2 rounded-full bg-[#965EA7] hover:bg-[#7A4986] text-white font-semibold px-5 py-2 text-sm disabled:opacity-60"
            >
              {loading ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
              {isEdit ? "Salvează modificările" : "Creează webinar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ===========================================================================
// Registrations drawer (right side) + CSV export
// ===========================================================================

function RegistrationsDrawer({ webinar, onClose, canMutate }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.get(`/admin/webinars/${webinar.id}/registrations`).then((r) => {
      if (cancelled) return;
      setRows(r.data?.registrations || []);
    }).catch((e) => {
      toast.error(e?.response?.data?.detail || "Eroare la încărcarea înscrișilor.");
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [webinar.id]);

  // M905 — local optimistic update so the badge flips immediately. Failure
  // toasts and reverts are handled by the helpers below.
  const patchRow = useCallback((id, patch) => {
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }, []);

  const handleStatusChange = useCallback(async (reg, newStatus) => {
    if (reg.status === newStatus) return;
    if (newStatus === "cancelled") {
      const ok = window.confirm(
        `Marchezi „${reg.first_name} ${reg.last_name}” ca „Nu mai vine”?\n\nÎnscrierea NU se șterge, doar își schimbă statusul.`,
      );
      if (!ok) return;
    }
    const previous = reg.status;
    patchRow(reg.id, { status: newStatus });
    try {
      await api.patch(
        `/admin/webinars/${webinar.id}/registrations/${reg.id}/status`,
        { status: newStatus },
      );
      toast.success(`Status actualizat: ${REG_STATUS_LABELS[newStatus] || newStatus}`);
    } catch (e) {
      patchRow(reg.id, { status: previous });
      toast.error(e?.response?.data?.detail || "Eroare la schimbarea statusului.");
    }
  }, [webinar.id, patchRow]);

  const handleConvertToLead = useCallback(async (reg) => {
    try {
      const r = await api.post(
        `/admin/webinars/${webinar.id}/registrations/${reg.id}/convert-to-lead`,
      );
      patchRow(reg.id, {
        converted_to_lead_id: r.data.lead_id,
        converted_at: new Date().toISOString(),
      });
      if (r.data.already_converted) {
        toast.info("Acest participant a fost deja convertit anterior.");
      } else if (r.data.possible_duplicate) {
        toast.warning(
          "Lead creat — există deja un lead cu același email (marcat ca posibil duplicat în CRM).",
        );
      } else {
        toast.success("Înscriere convertită în lead.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la conversie.");
    }
  }, [webinar.id, patchRow]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [
        r.first_name, r.last_name, r.email,
        r.school_name, r.institution,
        r.uat_name, r.city, r.county,
      ].filter(Boolean).some((v) => v.toLowerCase().includes(q))
    );
  }, [rows, query]);

  // Trigger a real GET on the export endpoint with cookies — same pattern as
  // the leads export. The browser handles the download natively.
  const handleExport = (format) => {
    window.open(`${API}${exportPath(`/admin/webinars/${webinar.id}/registrations/export`, null, format)}`, "_blank");
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div
        className="bg-white w-full max-w-2xl h-full overflow-y-auto shadow-2xl border-l border-[#E5E7EB]"
        onClick={(e) => e.stopPropagation()}
        data-testid="webinar-registrations-drawer"
      >
        <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB] sticky top-0 bg-white">
          <div>
            <div className="text-xs font-semibold text-[#965EA7] uppercase tracking-widest">Înscriși la webinar</div>
            <h3 className="font-display text-lg font-bold text-[#373A45] mt-0.5">{webinar.title}</h3>
            <div className="text-xs text-[#6B7280] mt-1">
              {webinar.date} · {rows.length} {rows.length === 1 ? "înscris" : "înscriși"}
              {webinar.capacity > 0 ? ` / ${webinar.capacity} locuri` : ""}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ExportButton
              onExport={handleExport}
              testid="webinar-export-csv"
              className="inline-flex items-center gap-1 rounded-full bg-[#373A45] hover:bg-[#1f2128] text-white text-xs font-semibold px-3 py-2"
            />
            <button onClick={onClose} className="text-[#6B7280] hover:text-[#373A45]"><X size={20} /></button>
          </div>
        </div>
        <div className="p-5 space-y-3">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Caută după nume, email, școală..."
            className={`${inp} text-sm`}
            data-testid="webinar-registrations-search"
          />
          {loading ? (
            <div className="text-center text-[#6B7280] py-10"><Loader2 className="inline animate-spin" size={16} /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-[#6B7280] py-10 text-sm">
              {rows.length === 0 ? "Niciun înscris încă." : "Nu există rezultate pentru căutare."}
            </div>
          ) : (
            <ul className="space-y-2">
              {filtered.map((r) => {
                const regStatus = r.status || "registered";
                const isConverted = !!r.converted_to_lead_id;
                const emailSent = !!r.confirmation_email_sent_at;
                const linkClicked = !!r.link_first_clicked_at;
                return (
                <li
                  key={r.id}
                  className="border border-[#E5E7EB] rounded-xl p-3 text-sm"
                  data-testid={`webinar-registration-${r.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-[#373A45]">{r.first_name} {r.last_name}</div>
                      <div className="text-[#6B7280] text-xs truncate">{r.email} · {r.phone}</div>
                      {(r.utm_source || r.utm_medium || r.utm_campaign || r.utm_term || r.utm_content) && (
                        <div
                          className="text-[#9CA3AF] text-[11px] truncate mt-0.5"
                          title={[
                            r.utm_source && `source: ${r.utm_source}`,
                            r.utm_medium && `medium: ${r.utm_medium}`,
                            r.utm_campaign && `campaign: ${r.utm_campaign}`,
                            r.utm_term && `term: ${r.utm_term}`,
                            r.utm_content && `content: ${r.utm_content}`,
                          ].filter(Boolean).join("\n")}
                          data-testid={`webinar-registration-utm-${r.id}`}
                        >
                          <span className="font-semibold">Sursă:</span>{" "}
                          {[r.utm_source, r.utm_medium, r.utm_campaign].filter(Boolean).join(" · ") || "—"}
                        </div>
                      )}
                      <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                        <span
                          className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${REG_STATUS_COLORS[regStatus] || ""}`}
                          data-testid={`webinar-registration-status-${r.id}`}
                        >
                          {REG_STATUS_LABELS[regStatus] || regStatus}
                        </span>
                        {isConverted && (
                          <Link
                            to={leadHref(r.converted_to_lead_id)}
                            className="inline-flex items-center gap-1 rounded-full bg-[#F0E7F4] px-2 py-0.5 text-[10px] font-semibold text-[#7A4986] hover:bg-[#E5D2EB]"
                            title="Deschide leadul în CRM"
                            data-testid={`webinar-registration-converted-${r.id}`}
                          >
                            <ArrowRightCircle size={10} /> Convertit în lead
                          </Link>
                        )}
                        {emailSent ? (
                          <span
                            className="inline-flex items-center gap-1 rounded-full bg-[#E7F4EE] px-2 py-0.5 text-[10px] font-semibold text-[#1F7A52]"
                            title={`Email confirmare trimis: ${new Date(r.confirmation_email_sent_at).toLocaleString("ro-RO")}${r.confirmation_email_via ? ` · ${r.confirmation_email_via}` : ""}`}
                            data-testid={`webinar-registration-email-sent-${r.id}`}
                          >
                            <MailCheck size={10} /> Email trimis
                          </span>
                        ) : (
                          <span
                            className="inline-flex items-center gap-1 rounded-full bg-[#FBEAE5] px-2 py-0.5 text-[10px] font-semibold text-[#B4541F]"
                            title={r.confirmation_email_via === "mock" ? "Email simulat (Resend neconfigurat)" : "Emailul de confirmare nu a fost încă trimis"}
                            data-testid={`webinar-registration-email-pending-${r.id}`}
                          >
                            <MailWarning size={10} /> {r.confirmation_email_via === "mock" ? "Email simulat" : "Email netrimis"}
                          </span>
                        )}
                        {linkClicked && (
                          <span
                            className="inline-flex items-center gap-1 rounded-full bg-[#EAF0FB] px-2 py-0.5 text-[10px] font-semibold text-[#185FA5]"
                            title={`A accesat linkul: ${new Date(r.link_first_clicked_at).toLocaleString("ro-RO")}${r.link_click_count ? ` · ${r.link_click_count} accesări` : ""}`}
                            data-testid={`webinar-registration-link-clicked-${r.id}`}
                          >
                            <MousePointerClick size={10} /> Link accesat
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <div className="text-[10px] text-[#9CA3AF] whitespace-nowrap">
                        {new Date(r.created_at).toLocaleString("ro-RO")}
                      </div>
                      {canMutate && (
                        <DropdownMenu modal={false}>
                          <DropdownMenuTrigger asChild>
                            <button
                              type="button"
                              className="h-7 w-7 inline-flex items-center justify-center rounded-full hover:bg-[#F5F6F6] text-[#6B7280]"
                              data-testid={`webinar-registration-menu-${r.id}`}
                              aria-label="Acțiuni"
                            >
                              <MoreVertical size={16} />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-56">
                            {regStatus !== "attended" && (
                              <DropdownMenuItem
                                onClick={() => handleStatusChange(r, "attended")}
                                data-testid={`webinar-reg-mark-attended-${r.id}`}
                              >
                                <UserCheck size={14} className="mr-2 text-[#519872]" />
                                Marchează prezent
                              </DropdownMenuItem>
                            )}
                            {regStatus !== "no_show" && (
                              <DropdownMenuItem
                                onClick={() => handleStatusChange(r, "no_show")}
                                data-testid={`webinar-reg-mark-no-show-${r.id}`}
                              >
                                <UserX size={14} className="mr-2 text-[#7A5A1B]" />
                                Marchează absent
                              </DropdownMenuItem>
                            )}
                            {regStatus !== "cancelled" && (
                              <DropdownMenuItem
                                onClick={() => handleStatusChange(r, "cancelled")}
                                data-testid={`webinar-reg-mark-cancelled-${r.id}`}
                              >
                                <Ban size={14} className="mr-2 text-[#E63946]" />
                                Nu mai vine
                              </DropdownMenuItem>
                            )}
                            {regStatus !== "registered" && (
                              <DropdownMenuItem
                                onClick={() => handleStatusChange(r, "registered")}
                                data-testid={`webinar-reg-mark-registered-${r.id}`}
                              >
                                <RotateCcw size={14} className="mr-2 text-[#3F8EBA]" />
                                Revino la înscris
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              disabled={isConverted}
                              onClick={() => handleConvertToLead(r)}
                              data-testid={`webinar-reg-convert-to-lead-${r.id}`}
                            >
                              <ArrowRightCircle size={14} className="mr-2 text-[#965EA7]" />
                              {isConverted ? "Deja convertit în lead" : "Convertește în lead"}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </div>
                  {/* M900 — show structured geo + school. Fall back to the
                      legacy free-text fields for pre-M900 rows. */}
                  {(r.school_name || r.institution || r.uat_name || r.city || r.county || r.position) && (
                    <div className="mt-2 text-xs text-[#373A45]">
                      {[
                        r.school_name || r.institution,
                        r.uat_name || r.city,
                        r.county,
                        r.position,
                      ].filter(Boolean).join(" · ")}
                    </div>
                  )}
                  {r.referral_source && (
                    <div className="mt-1 text-[11px] text-[#6B7280] italic">
                      A aflat: {r.referral_source}
                      {r.referral_source_detail ? ` — ${r.referral_source_detail}` : ""}
                    </div>
                  )}
                </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <fieldset className="border border-[#E5E7EB] rounded-2xl p-4">
      <legend className="px-2 text-xs font-semibold text-[#965EA7] uppercase tracking-widest">{title}</legend>
      {children}
    </fieldset>
  );
}

function Field({ label, children, wide = false }) {
  return (
    <label className={`block ${wide ? "md:col-span-2" : ""}`}>
      <span className="text-xs font-semibold text-[#373A45] uppercase tracking-widest">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

const inp =
  "w-full bg-white border border-[#E5E7EB] rounded-xl px-3 py-2.5 text-[#373A45] focus:ring-2 focus:ring-[#965EA7] focus:border-transparent outline-none";
